/**
 * PATH: apps/quote/src/pages/claims/claims-list-page/types.ts
 * PURPOSE: Types and config for ClaimsListPage — list row derived from the
 *          generated FE↔BE contract (BackendSchemas) (S2 rank 7).
 * LANDMINE: `job` is a SerializerMethodField the OpenAPI schema types as
 *          string; it is overridden to the real nested summary shape.
 *          progress_percent is numeric at runtime. Do not re-hand-type
 *          the other fields — extend the schema type instead.
 */

import type { BackendSchemas } from '@quotech/api'
import {
  Edit,
  Clock,
  Eye,
  CheckCircle,
  Send,
  DollarSign,
  XCircle,
} from 'lucide-react'

type ApiClaimList = BackendSchemas['PaymentClaimList']

/** All serializer fields are present at runtime (DRF emits every listed
 *  field), so the schema's blank-allowed `?` marks are stripped. */
export interface PaymentClaim
  extends Omit<Required<ApiClaimList>, 'job' | 'progress_percent'> {
  job: {
    id: string
    job_number: string
    name: string
    client_name: string
  }
  progress_percent: number
}

/** Backend PaymentClaim.is_editable — the LIST serializer does not send it
 *  (contract migration caught this drift: the old hand-typed `is_editable`
 *  was always undefined, silently hiding Edit/Delete). Mirror of
 *  claims/models.py is_editable: draft or rejected. */
export const isClaimEditable = (claim: Pick<PaymentClaim, 'status'>) =>
  claim.status === 'draft' || claim.status === 'rejected'

export interface ClaimsListResponse {
  count: number
  next: string | null
  previous: string | null
  results: PaymentClaim[]
}

export const STATUS_CONFIG = {
  draft: { label: 'Draft', icon: Edit, color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
  submitted: { label: 'Submitted', icon: Clock, color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' },
  under_review: { label: 'Under Review', icon: Eye, color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300' },
  approved: { label: 'Approved', icon: CheckCircle, color: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' },
  sent: { label: 'Sent to Client', icon: Send, color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300' },
  paid: { label: 'Paid', icon: DollarSign, color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300' },
  rejected: { label: 'Rejected', icon: XCircle, color: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300' },
  cancelled: { label: 'Cancelled', icon: XCircle, color: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400' },
} as const
